// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The floor is the room's, not the reference space's (14 Sep 2026).
//
// `floorAlignment.ts` names two floor faults — a stale one and a broken one — and answers the
// broken one from what it knows about the *person*: a height no person could have is corrected
// against the height this person is known to have. The 19:19 session on a Quest 3 produced a third
// fault that neither answer reaches: after a Meta recentre the `local-floor` space came back with
// its floor 0.42 m above the room's, and a standing player at 1.62 m read 1.20 m — a believable
// height, a seated one, so nothing corrected it, and the cabinet stood on the invented floor with
// its lockdown bar at the player's chest: "brings machine table top to eye level instead of table
// legs touching ground (should use Quest's ground location)".
//
// The headset does know where the ground is. A session granted `plane-detection` is handed the
// room scan's planes every frame, and the one labelled `floor` is the ground the person set up
// the room on. Its height in the space being presented is the error in that space, directly —
// no person, no memory, no plausibility argument — and the correction is to move the presented
// space until that plane reads zero. This file chooses the plane and decides when a reading is
// steady enough to act on; `xrFrameLoop.ts` reads the planes, `FloorAlignment.alignToFloorPlane`
// moves the space, and `XRRuntime` connects the three.
//
// Pure. Planes arrive already in the presented space: a height, a label if the runtime gives one,
// an orientation, and the polygon's corners on the ground.

/** One detected plane, in the space being presented. */
export interface PlaneReading {
  /** The plane's height in the presented space. */
  readonly y: number
  /** The runtime's word for it — `floor`, `wall`, `table` — or undefined on a runtime without labels. */
  readonly label: string | undefined
  readonly orientation: 'horizontal' | 'vertical' | undefined
  /** The plane's outline, projected onto the ground, in the presented space. */
  readonly corners: readonly { readonly x: number; readonly z: number }[]
}

/** A floor without a label has to sit this far below the eyes to be believed as one, metres. */
export const FLOOR_BELOW_EYES_MIN = 0.8
export const FLOOR_BELOW_EYES_MAX = 2.3

/**
 * The height of the room's floor under the player, or `undefined` when the scan has no floor.
 *
 * A plane the runtime calls `floor` is taken at its word. Without labels, a horizontal plane the
 * right distance below the eyes is a floor. Among candidates the one the player is standing on
 * — whose outline contains the head's ground position — wins; otherwise the nearest outline.
 */
export function pickFloor(
  planes: readonly PlaneReading[],
  head: { readonly x: number; readonly y: number; readonly z: number },
): number | undefined {
  const labelled = planes.filter((p) => p.label !== undefined && p.label.toLowerCase() === 'floor')
  const candidates =
    labelled.length > 0
      ? labelled
      : planes.filter(
          (p) =>
            p.orientation !== 'vertical'
            && head.y - p.y >= FLOOR_BELOW_EYES_MIN
            && head.y - p.y <= FLOOR_BELOW_EYES_MAX,
        )
  if (candidates.length === 0) return undefined
  const under = candidates.find((p) => contains(p.corners, head.x, head.z))
  if (under !== undefined) return under.y
  let best = candidates[0]!
  let bestDistance = Number.POSITIVE_INFINITY
  for (const p of candidates) {
    const d = distanceToOutline(p.corners, head.x, head.z)
    if (d < bestDistance) {
      bestDistance = d
      best = p
    }
  }
  return best.y
}

/** Ray-crossing point-in-polygon on the ground. An outline with fewer than three corners contains nothing. */
export function contains(corners: readonly { readonly x: number; readonly z: number }[], x: number, z: number): boolean {
  if (corners.length < 3) return false
  let inside = false
  for (let i = 0, j = corners.length - 1; i < corners.length; j = i, i += 1) {
    const a = corners[i]!
    const b = corners[j]!
    const crosses = a.z > z !== b.z > z && x < ((b.x - a.x) * (z - a.z)) / (b.z - a.z) + a.x
    if (crosses) inside = !inside
  }
  return inside
}

function distanceToOutline(corners: readonly { readonly x: number; readonly z: number }[], x: number, z: number): number {
  if (corners.length === 0) return Number.POSITIVE_INFINITY
  let cx = 0
  let cz = 0
  for (const c of corners) {
    cx += c.x
    cz += c.z
  }
  return Math.hypot(cx / corners.length - x, cz / corners.length - z)
}

/**
 * A floor reading has to be this far from zero before the space is moved, metres.
 *
 * Three centimetres: under it the runtime's floor and the room's agree as well as a scan can be
 * trusted to, and a correction would be a wobble. The 19:19 fault was 0.42 m.
 */
export const FLOOR_PLANE_TOLERANCE = 0.03

/** How long a reading has to hold steady, within `FLOOR_PLANE_JITTER`, before it is acted on. */
export const FLOOR_PLANE_STEADY_S = 0.5
export const FLOOR_PLANE_JITTER = 0.01

/** After a move, how long before the floor is read for another, so one move settles before the next. */
export const FLOOR_PLANE_COOLDOWN_S = 1

/**
 * Watches the floor plane's height frame by frame and says when to move the presented space, and
 * by how much. One answer per steady disagreement; then a cooldown, and the reading — which the
 * move should have brought to zero — is watched again.
 */
export class FloorPlaneWatch {
  private steadyFor = 0
  private steadyAt: number | undefined
  private cooldown = 0

  /**
   * The frame's floor reading, or `undefined` for a frame without one. Returns the offset to
   * apply — the plane's height — when a reading has held steady and disagrees with zero.
   */
  advance(y: number | undefined, frameSeconds: number): number | undefined {
    if (this.cooldown > 0) {
      this.cooldown = Math.max(0, this.cooldown - frameSeconds)
      return undefined
    }
    if (y === undefined) {
      this.steadyFor = 0
      this.steadyAt = undefined
      return undefined
    }
    if (this.steadyAt === undefined || Math.abs(y - this.steadyAt) > FLOOR_PLANE_JITTER) {
      this.steadyAt = y
      this.steadyFor = 0
      return undefined
    }
    this.steadyFor += frameSeconds
    if (this.steadyFor < FLOOR_PLANE_STEADY_S) return undefined
    this.steadyFor = 0
    this.steadyAt = undefined
    if (Math.abs(y) <= FLOOR_PLANE_TOLERANCE) return undefined
    this.cooldown = FLOOR_PLANE_COOLDOWN_S
    return y
  }

  reset(): void {
    this.steadyFor = 0
    this.steadyAt = undefined
    this.cooldown = 0
  }
}
